import { useState } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { authApi } from "api/auth/api";
import cn from "classnames";
import { Button } from "components/ui/Button/Button";
import { FormField } from "components/ui/FormField";
import { Loader } from "components/ui/Loader/Loader";
import { SIGN_IN_ROUTE } from "constants/routesValues";
import { SITE_NAME } from "constants/siteName";
import { VALIDATION_SCHEMA } from "constants/validationSchema";
import { useSiteName } from "context/SiteName.context";
import { createRouteAction } from "utils/history.utils";
import { QueryParamsManager } from "utils/queryParams.lib";
import { getRecaptchaToken } from "utils/recaptcha";
import style from "./ResetPassword.module.scss";

export type PossibleUrlQueryParams = {
  token: string;
  email: string;
};

type ResetPasswordFormValues = {
  newPassword: string;
  confirmPassword: string;
};

export const ResetPassword = () => {
  const { siteName } = useSiteName();
  const isChineseBrothers = siteName === SITE_NAME.CHINESE_BROTHERS;
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [isResetPasswordFetching, setIsResetPasswordFetching] = useState<boolean>(false);
  const qpm = new QueryParamsManager<PossibleUrlQueryParams, keyof PossibleUrlQueryParams>(window.location.href, [
    "token",
    "email",
  ]);

  const formMethods = useForm<ResetPasswordFormValues>({
    defaultValues: {
      newPassword: "",
      confirmPassword: "",
    },
    mode: "onSubmit",
  });

  const onSubmit = async (_data: ResetPasswordFormValues) => {
    if (_data.newPassword !== _data.confirmPassword) {
      formMethods.setError("confirmPassword", { message: "Passwords must be equal." });
      return;
    } else {
      setErrorMessage("");
      setIsResetPasswordFetching(true);

      let recaptcha: string;
      try {
        recaptcha = await getRecaptchaToken("RESET_PASSWORD");
      } catch {
        setIsResetPasswordFetching(false);
        setErrorMessage("Error with reCAPTCHA verification.");
        return;
      }

      await authApi
        .resetPassword({
          token: String(qpm.get("token")),
          email: String(qpm.get("email")),
          password: _data.newPassword,
          password_confirmation: _data.confirmPassword,
          recaptcha,
        })
        .then((response) => {
          setIsResetPasswordFetching(false);
          if (response.status === 200) {
            createRouteAction(SIGN_IN_ROUTE);
          }
        })
        .catch((error) => {
          setIsResetPasswordFetching(false);
          const status = error.response?.status;
          const errors = error.response?.data?.errors;

          if (status === 422 && errors?.recaptcha) {
            setErrorMessage(errors.recaptcha[0] ?? "reCAPTCHA verification failed.");
            return;
          }

          if (status === 500) {
            setErrorMessage("Server error. Please try again later.");
            return;
          }

          setErrorMessage(error.response?.data?.message || "Something went wrong.");
        });
    }
  };

  return (
    <div
      className={cn(style.resetPasswordWrapper, {
        [style.resetPasswordWrapperChineseBrothers]: isChineseBrothers,
      })}
    >
      <h3 className={style.resetPasswordTitle}>Reset password</h3>
      <p className={style.resetPasswordDescription}>
        At least 8 characters, including uppercase and <br className={style.resetPasswordDescriptionBreak} /> lowercase
        letters, 1 number, 1 special character.
      </p>
      <FormProvider {...formMethods}>
        <form id="resetPasswordForm" onSubmit={formMethods.handleSubmit(onSubmit)}>
          <Controller
            name="newPassword"
            control={formMethods.control}
            rules={VALIDATION_SCHEMA.password}
            render={({ field, fieldState }) => (
              <FormField
                name={field.name}
                labelTitle="New password"
                type="password"
                isPassword
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                error={fieldState.error}
              />
            )}
          />
          <Controller
            name="confirmPassword"
            control={formMethods.control}
            rules={VALIDATION_SCHEMA.password}
            render={({ field, fieldState }) => (
              <FormField
                name={field.name}
                labelTitle="Confirm Password"
                type="password"
                isPassword
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                error={fieldState.error}
              />
            )}
          />
          <Button
            disabled={isResetPasswordFetching}
            text={isResetPasswordFetching ? "Please wait" : "Reset Password"}
            type="submit"
            className={cn(style.formButton, {
              [style.formButtonChineseBrothers]: isChineseBrothers,
            })}
          >
            {isResetPasswordFetching && <Loader className={style.loader} />}
          </Button>
        </form>
        {errorMessage && <p className={style.errorMessage}>{errorMessage}</p>}
        <p className={style.formDescriptionLink} onClick={() => createRouteAction(SIGN_IN_ROUTE)}>
          Back to <span onClick={() => createRouteAction(SIGN_IN_ROUTE)}>Sign in</span>
        </p>
      </FormProvider>
    </div>
  );
};
